import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import axios from "axios";
import SiteFooter from "../components/SiteFooter";
import { CityAutocomplete } from "../components/CityAutocomplete";

const Global = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;700;900&display=swap');
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #FFFBF0; font-family: 'Pretendard', 'Malgun Gothic', sans-serif; }
`;

const API_BASE = process.env.REACT_APP_API_URL || "";

function parseTime(val: string) {
  const s = val.replace(/\s/g, "");
  if (!s) return { hour: 99, minute: 0 };
  let h: number, m: number;
  if (s.includes(":")) {
    const parts = s.split(":");
    h = parseInt(parts[0]); m = parseInt(parts[1] || "0");
  } else if (/^\d{3,4}$/.test(s)) {
    const p = s.padStart(4, "0");
    h = parseInt(p.slice(0, 2)); m = parseInt(p.slice(2));
  } else {
    h = parseInt(s); m = 0;
  }
  return {
    hour: (!isNaN(h) && h >= 0 && h <= 23) ? h : 99,
    minute: (!isNaN(m) && m >= 0 && m <= 59) ? m : 0,
  };
}

function parseBirthDate(val: string) {
  const sep = val.includes("/") ? "/" : val.includes(".") ? "." : val.includes("-") ? "-" : null;
  if (sep) { const [y,m,d] = val.split(sep).map(Number); return { year:y||0,month:m||0,day:d||0 }; }
  if (val.length === 8) return { year:Number(val.slice(0,4)),month:Number(val.slice(4,6)),day:Number(val.slice(6,8)) };
  return { year:0,month:0,day:0 };
}

/* ── 서비스 정의 ──────────────────────────────────────────── */
const SERVICES = [
  { key: "퀵사주_연애운",   label: "연애운",  iconImg: "/woo_y_1.png", price: 1000, color: "#e03e7c", img: "/woo_08.png", desc: "이상형을 어디서 만날까?\n어떻게 생겼을까? 언제 만날까?", needPartner: false },
  { key: "퀵사주_직장운",   label: "직장운",  iconImg: "/woo_y_5.png", price: 1000, color: "#2563eb", img: "/woo_05.png", desc: "이직은 언제?\n재직 중이라면 승진할까?",              needPartner: false },
  { key: "퀵사주_재물운",   label: "재물운",  iconImg: "/woo_y_3.png", price: 1000, color: "#059669", img: "/woo_05.png", desc: "투자 성공할까?\n계약운, 사업 매출은?",              needPartner: false },
  { key: "퀵사주_연애궁합", label: "연애궁합", iconImg: "/woo_y_2.png", price: 2000, color: "#7c3aed", img: "/woo_02.png", desc: "우리의 궁합점수는?\n성격과 속궁합은 맞을까?",       needPartner: true, ageLimit: true },
];


const BIRTH_TYPES = [
  { value: "solar",      label: "양력" },
  { value: "lunar",      label: "음력" },
  { value: "lunar_leap", label: "음력 윤달" },
];

/* ── keyframes ──────────────────────────────────────────────── */
const floatQ = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
`;

/* ── styled components ──────────────────────────────────────── */
const DancheongBar = styled.div`
  height: 6px;
  width: 100%;
  background: repeating-linear-gradient(90deg,#CC3333 0 20px,#C8901E 20px 40px,#4A7C6B 40px 60px,#2E6DA4 60px 80px,#C8901E 80px 100px);
`;

const Wrap = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 0 0 80px;
  background: #FFFBF0;
`;

const NavBar = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 16px 0;
  margin-bottom: 4px;
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.7);
  border: 1.5px solid #d8cfe8;
  border-radius: 20px;
  padding: 7px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  color: #5a4a72;
  font-family: inherit;
  box-shadow: 0 2px 8px rgba(74,32,96,0.08);
  & + & { margin-left: 8px; }
`;

const PageTitle = styled.div`
  font-size: 15px;
  font-weight: 900;
  color: #4a2060;
  margin-left: auto;
`;

/* 전체 씬 히어로 */
const HeroSceneWrap = styled.div`width:100%; max-width:560px; position:relative; overflow:hidden; margin:0 auto;`;
const HeroSceneImg = styled.img`width:100%; display:block; object-fit:cover;`;
const HeroSceneOverlay = styled.div`position:absolute; bottom:0; left:0; right:0; height:120px; background:linear-gradient(to bottom,transparent,#FFFBF0);`;
const HeroTitleBlock = styled.div`position:absolute; bottom:16px; left:0; right:0; text-align:center; z-index:2;`;
const HeroTitle = styled.div`font-family:'Noto Serif KR',serif; font-size:1.6rem; font-weight:900; color:#2C1810; text-shadow:0 2px 8px rgba(255,251,240,0.8); margin-bottom:3px;`;
const HeroSub = styled.div`font-size:0.78rem; color:#7A5C42; font-weight:600; text-shadow:0 1px 4px rgba(255,251,240,0.9);`;
const SectionLabel = styled.div`font-family:'Noto Serif KR',serif; font-size:0.9rem; font-weight:900; color:#2C1810; padding:18px 16px 10px; display:flex; align-items:center; gap:8px; &::after{content:'';flex:1;height:1.5px;background:linear-gradient(90deg,#C8901E44,transparent);}`;

/* 결과 헤더용 누끼 이미지 */
const ResultHeroImg = styled.img`width:90px; height:90px; object-fit:contain; filter:drop-shadow(0 4px 14px rgba(200,144,30,0.3)); margin-bottom:8px;`;
const SvcIconImg = styled.img`width:32px; height:32px; object-fit:contain; filter:drop-shadow(0 2px 6px rgba(0,0,0,0.15)); margin-bottom:3px;`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── 서비스 카드 — 이미지가 상단에 크게 ─────────────────────── */
const CardGrid = styled.div`display:grid; grid-template-columns:1fr 1fr; gap:14px; padding:0 16px;`;

const SvcCard = styled.button<{ $color: string; $idx: number }>`
  background: #fff;
  border: 1.5px solid ${p => p.$color}30;
  border-radius: 20px; padding: 0;
  cursor: pointer; text-align: left;
  overflow: hidden; font-family: inherit;
  animation: ${fadeUp} 0.35s ease both;
  animation-delay: ${p => p.$idx * 0.07}s;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(0,0,0,0.07);
  &:hover { transform: translateY(-6px); box-shadow: 0 14px 32px ${p => p.$color}28; border-color: ${p => p.$color}60; }
`;
const SvcCardImg = styled.div<{ $src: string }>`
  width:100%; height:120px;
  background-image:url(${p => p.$src}); background-size:cover; background-position:center top;
  border-radius:18px 18px 0 0; position:relative;
  &::after{content:'';position:absolute;bottom:0;left:0;right:0;height:36px;background:linear-gradient(to bottom,transparent,#fff);}
`;
const SvcCardBody = styled.div<{ $color: string }>`padding:9px 13px 13px; border-top:2px solid ${p => p.$color}18;`;
const SvcLabel = styled.div<{ $color: string }>`font-size:10px; font-weight:600; color:${p => p.$color}; margin-bottom:3px; opacity:0.85;`;
const SvcDesc = styled.div<{ $color: string }>`font-size:12.5px; font-weight:800; color:#2d1f3d; line-height:1.55; white-space:pre-line; margin-bottom:9px;`;
const SvcPrice = styled.div<{ $color: string }>`display:inline-flex; align-items:center; background:${p => p.$color}; color:#fff; border-radius:20px; padding:4px 11px; font-size:11px; font-weight:700;
`;

/* ── 입력 폼 ──────────────────────────────────────────────────── */
const FormArea = styled.div`padding: 0 16px;`;

const ServiceBadge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${p => p.$color}10;
  border: 1.5px solid ${p => p.$color}30;
  border-radius: 14px;
  padding: 12px 16px;
  margin-bottom: 18px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 20px 20px 16px;
  box-shadow: 0 4px 24px rgba(74,32,96,0.10);
  margin-bottom: 14px;
`;

const CardTopBar = styled.div<{ $color: string }>`
  font-size: 13px;
  font-weight: 800;
  color: ${p => p.$color};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 14px;
    background: ${p => p.$color};
    border-radius: 2px;
  }
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9c7b5a;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 16px 0 8px;
  &:first-child { margin-top: 0; }
`;

const FieldRow = styled.div`display: flex; gap: 8px; margin-bottom: 10px;`;
const Field = styled.div`flex: 1; display: flex; flex-direction: column; gap: 4px;`;
const Label = styled.label`font-size: 12px; color: #5a4a3a; font-weight: 600;`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e0d5c8;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #3d2b1f;
  background: #fdfaf7;
  font-family: inherit;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #c09a6b; }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e0d5c8;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #3d2b1f;
  background: #fdfaf7;
  font-family: inherit;
  &:focus { outline: none; border-color: #c09a6b; }
`;

const PartnerDivider = styled.div`
  border-top: 2px dashed #e0d5c8;
  margin: 16px -20px 0;
  padding: 16px 20px 0;
  background: linear-gradient(180deg, #f8f9fa 0%, #fff 100%);
`;

const ConsentBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.8rem;
  color: #5a4a3a;
  line-height: 1.5;
  margin: 18px 0 0;
  cursor: pointer;
  input { margin-top: 2px; accent-color: #c09a6b; width: 16px; height: 16px; flex-shrink: 0; }
`;

const ErrMsg = styled.div`
  padding: 10px 14px;
  background: #fff5f8;
  border: 1px solid #f5c2cf;
  border-radius: 10px;
  font-size: 13px;
  color: #c0392b;
  margin-top: 12px;
`;

const SubmitBtn = styled.button<{ $color: string; $disabled?: boolean }>`
  width: 100%;
  padding: 15px;
  background: ${p => p.$disabled ? "#ccc" : `linear-gradient(135deg, ${p.$color}, ${p.$color}cc)`};
  color: #fff;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 800;
  cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
  font-family: inherit;
  box-shadow: ${p => p.$disabled ? "none" : `0 4px 18px ${p.$color}44`};
  margin-top: 16px;
  transition: opacity 0.2s;
  &:hover { opacity: ${p => p.$disabled ? 1 : 0.92}; }
`;

/* ── 결과 화면 ──────────────────────────────────────────────── */
const ResultCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 32px 24px;
  box-shadow: 0 4px 24px rgba(74,32,96,0.10);
  text-align: center;
  margin: 0 16px;
`;

/* ── 인터페이스 ─────────────────────────────────────────────── */
interface PersonForm {
  name: string;
  gender: string;
  birth_type: string;
  birth_date: string;
  birth_time: string;
  birth_time_unknown: boolean;
  city: string;
}

const emptyPerson = (): PersonForm => ({
  name: "", gender: "female", birth_type: "solar",
  birth_date: "",
  birth_time: "",
  birth_time_unknown: false,
  city: "서울",
});

/* ── PersonFormBlock (모듈 레벨에 정의 — 성능 핵심) ─────────── */
interface PFBProps {
  title: string;
  data: PersonForm;
  onChange: (d: PersonForm) => void;
  accentColor: string;
  email?: string;
  onEmailChange?: (v: string) => void;
  onSurvey?: () => void;
}

function PersonFormBlock({ title, data, onChange, accentColor, email, onEmailChange, onSurvey }: PFBProps) {
  return (
    <Card>
      <CardTopBar $color={accentColor}>{title}</CardTopBar>

      <SectionTitle>기본 정보</SectionTitle>
      <FieldRow>
        <Field>
          <Label>이름 *</Label>
          <Input
            placeholder="홍길동"
            value={data.name}
            onChange={e => onChange({ ...data, name: e.target.value })}
            inputMode="text"
            autoComplete="name"
          />
        </Field>
        <Field style={{ maxWidth: 110 }}>
          <Label>성별 *</Label>
          <Select value={data.gender} onChange={e => onChange({ ...data, gender: e.target.value })}>
            <option value="female">여성</option>
            <option value="male">남성</option>
          </Select>
        </Field>
      </FieldRow>

      {onEmailChange !== undefined && (
        <FieldRow>
          <Field>
            <Label>결과 받을 이메일 *</Label>
            <Input
              type="email"
              placeholder="example@email.com"
              value={email ?? ""}
              onChange={e => onEmailChange(e.target.value)}
              inputMode="email"
              autoComplete="email"
            />
            <div style={{ fontSize: 11, color: "#9a8aaa", marginTop: 3 }}>
              결제 완료 후 이메일로 결과지가 발송됩니다
            </div>
          </Field>
        </FieldRow>
      )}

      <SectionTitle>생년월일시</SectionTitle>
      <FieldRow>
        <Field>
          <Label>구분</Label>
          <Select value={data.birth_type} onChange={e => onChange({ ...data, birth_type: e.target.value })}>
            {BIRTH_TYPES.map(bt => (
              <option key={bt.value} value={bt.value}>{bt.label}</option>
            ))}
          </Select>
        </Field>
        <Field style={{ flex: 2 }}>
          <Label>생년월일 * (예: 1990/05/15)</Label>
          <Input
            placeholder="1990/05/15" maxLength={10}
            value={data.birth_date}
            onChange={e => onChange({ ...data, birth_date: e.target.value })}
          />
        </Field>
      </FieldRow>
      <FieldRow style={{ alignItems: "center", marginBottom: 4 }}>
        <Label style={{ marginBottom: 0 }}>태어난 시간</Label>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#7a6654", cursor: "pointer", marginLeft: 8 }}>
          <input type="checkbox" checked={data.birth_time_unknown}
            onChange={e => onChange({ ...data, birth_time_unknown: e.target.checked })}
            style={{ accentColor: "#c09a6b" }} />
          시간 모름
        </label>
        {data.birth_time_unknown && onSurvey && (
          <button type="button" onClick={onSurvey}
            style={{marginLeft:"auto",padding:"4px 10px",border:"1px solid #c09a6b",borderRadius:6,background:"#fff8f0",color:"#c09a6b",fontSize:"0.73rem",fontWeight:700,cursor:"pointer"}}>
            ✨ 시간 추정 도움받기
          </button>
        )}
      </FieldRow>
      <FieldRow>
        <Field>
          <Input
            placeholder="예: 12:50" maxLength={5}
            value={data.birth_time_unknown ? "" : data.birth_time}
            disabled={data.birth_time_unknown}
            style={data.birth_time_unknown ? { background: "#f5f0ea", color: "#b0a090", cursor: "not-allowed" } : {}}
            onChange={e => onChange({ ...data, birth_time: e.target.value })}
          />
        </Field>
      </FieldRow>

      <SectionTitle>지역 (시간 보정용)</SectionTitle>
      <FieldRow style={{ marginBottom: 0 }}>
        <Field>
          <CityAutocomplete
            value={data.city}
            onChange={v => onChange({ ...data, city: v })}
          />
        </Field>
      </FieldRow>
    </Card>
  );
}

/* ── QuickPage ───────────────────────────────────────────────── */
export default function QuickPage() {
  const navigate = useNavigate();
  const [step, setStep]     = useState<0 | 1>(0);
  const [svcKey, setSvcKey] = useState<string>("");
  const [person1, setPerson1] = useState<PersonForm>(emptyPerson());
  const [person2, setPerson2] = useState<PersonForm>(emptyPerson());
  const [email, setEmail]   = useState<string>("");
  const [consent, setConsent] = useState(false);
  const [err, setErr]       = useState<string>("");
  const [paying, setPaying]   = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<{ text: string; price: number } | null>(null);
  const [result, setResult]   = useState<{
    emailSent: boolean;
    reportHtml: string;
    consultationId: number | null;
  } | null>(null);
  /* ─── 시주 추정 설문 state ─── */
  const [surveying,     setSurveying]     = useState<"p1"|"p2"|null>(null);
  const [surveyQs,      setSurveyQs]      = useState<{id:number;question:string;choices:string[]}[]>([]);
  const [surveyAns,     setSurveyAns]     = useState<number[]>(Array(10).fill(-1));
  const [surveyResults, setSurveyResults] = useState<any[]>([]);

  const openSurveyFor = async (target: "p1"|"p2") => {
    if (surveyQs.length === 0) {
      try {
        const r = await axios.get(`${API_BASE}/api/v1/saju/hour-survey`);
        setSurveyQs(r.data.questions);
      } catch { return; }
    }
    setSurveyAns(Array(10).fill(-1));
    setSurveyResults([]);
    setSurveying(target);
  };
  const closeSurvey = () => setSurveying(null);
  const submitSurvey = async () => {
    if (surveyAns.some(a => a < 0)) return;
    try {
      const r = await axios.post(`${API_BASE}/api/v1/saju/estimate-hour`, { answers: surveyAns });
      setSurveyResults(r.data.recommendations);
    } catch {}
  };
  const applySurveyResult = (birthHour: number) => {
    const timeStr = String(birthHour).padStart(2,"0") + ":00";
    if (surveying === "p1") setPerson1(p => ({ ...p, birth_time: timeStr, birth_time_unknown: false }));
    else if (surveying === "p2") setPerson2(p => ({ ...p, birth_time: timeStr, birth_time_unknown: false }));
    setSurveying(null);
  };

  const svc = SERVICES.find(s => s.key === svcKey);

  function selectService(key: string) {
    setSvcKey(key);
    setPerson1(emptyPerson());
    setPerson2(emptyPerson());
    setEmail("");
    setConsent(false);
    setErr("");
    setResult(null);
    setStep(1);
  }

  function validateForm(): string | null {
    if (!consent) return "이용약관 및 개인정보 처리방침에 동의해주세요.";
    const p = person1;
    if (!p.name.trim()) return "이름을 입력해주세요.";
    const { year: yr, month: mo, day: dy } = parseBirthDate(p.birth_date);
    if (!yr || yr < 1900 || yr > 2020) return "생년월일을 올바르게 입력해주세요. 예: 1990/05/15";
    if (!mo || mo < 1 || mo > 12) return "생월을 올바르게 입력해주세요.";
    if (!dy || dy < 1 || dy > 31) return "생일을 올바르게 입력해주세요.";
    if (!email.trim()) return "결과를 받을 이메일 주소를 입력해주세요.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return "올바른 이메일 주소를 입력해주세요.";
    if (svc?.needPartner) {
      const p2 = person2;
      if (!p2.name.trim()) return "상대방 이름을 입력해주세요.";
      const { year: yr2, month: mo2, day: dy2 } = parseBirthDate(p2.birth_date);
      if (!yr2 || yr2 < 1900 || yr2 > 2020) return "상대방 생년월일을 올바르게 입력해주세요.";
      if (!mo2 || mo2 < 1 || mo2 > 12) return "상대방 생월을 올바르게 입력해주세요.";
      if (!dy2 || dy2 < 1 || dy2 > 31) return "상대방 생일을 올바르게 입력해주세요.";
      if (svc?.ageLimit) {
        const now = new Date().getFullYear();
        if (now - yr < 20 || now - yr2 < 20) return "연애궁합은 만 20세 이상만 이용 가능합니다.";
      }
    }
    return null;
  }

  async function callAnalyze(impUid?: string, merchantUid?: string) {
    setPaying(true);
    try {
      const p1d = parseBirthDate(person1.birth_date);
      const body: any = {
        service_type: svcKey,
        email: email.trim(),
        imp_uid: impUid,
        merchant_uid: merchantUid,
        person1: {
          name: person1.name,
          gender: person1.gender,
          birth_year:   p1d.year,
          birth_month:  p1d.month,
          birth_day:    p1d.day,
          birth_hour:   person1.birth_time_unknown ? 99 : parseTime(person1.birth_time).hour,
          birth_minute: person1.birth_time_unknown ? 0 : parseTime(person1.birth_time).minute,
          birth_type:   person1.birth_type,
          city:         person1.city,
        },
      };
      if (svc?.needPartner) {
        const p2d = parseBirthDate(person2.birth_date);
        body.person2 = {
          name: person2.name,
          gender: person2.gender,
          birth_year:   p2d.year,
          birth_month:  p2d.month,
          birth_day:    p2d.day,
          birth_hour:   person2.birth_time_unknown ? 99 : parseTime(person2.birth_time).hour,
          birth_minute: person2.birth_time_unknown ? 0 : parseTime(person2.birth_time).minute,
          birth_type:   person2.birth_type,
          city:         person2.city,
        };
      }
      const res = await axios.post(`${API_BASE}/api/v1/quick/analyze`, body);
      setResult({
        emailSent:      res.data.email_sent === true,
        reportHtml:     res.data.report_html || "",
        consultationId: res.data.consultation_id ?? null,
      });
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "분석 중 오류가 발생했습니다.");
      setPaying(false);
    }
  }

  async function handlePreview() {
    setErr("");
    const e = validateForm();
    if (e) { setErr(e); return; }
    setPreviewing(true);
    try {
      const p1d = parseBirthDate(person1.birth_date);
      const body: any = {
        service_type: svcKey,
        person1: {
          name: person1.name, gender: person1.gender,
          birth_year: p1d.year, birth_month: p1d.month, birth_day: p1d.day,
          birth_hour: person1.birth_time_unknown ? 99 : parseTime(person1.birth_time).hour,
          birth_minute: person1.birth_time_unknown ? 0 : parseTime(person1.birth_time).minute,
          birth_type: person1.birth_type, city: person1.city,
        },
      };
      if (svc?.needPartner) {
        const p2d = parseBirthDate(person2.birth_date);
        body.person2 = {
          name: person2.name, gender: person2.gender,
          birth_year: p2d.year, birth_month: p2d.month, birth_day: p2d.day,
          birth_hour: person2.birth_time_unknown ? 99 : parseTime(person2.birth_time).hour,
          birth_minute: person2.birth_time_unknown ? 0 : parseTime(person2.birth_time).minute,
          birth_type: person2.birth_type, city: person2.city,
        };
      }
      const res = await axios.post(`${API_BASE}/api/v1/quick/preview`, body);
      setPreview({ text: res.data.preview_text, price: res.data.price });
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "분석 중 오류가 발생했습니다.");
    } finally {
      setPreviewing(false);
    }
  }

  function handlePay() {
    setErr("");
    const e = validateForm();
    if (e) { setErr(e); return; }

    if (!svc) return;

    const IMP = (window as any).IMP;
    if (!IMP) {
      // 결제 모듈 없으면 직접 처리 (개발/테스트)
      callAnalyze();
      return;
    }

    IMP.init(process.env.REACT_APP_IMP_CODE || "imp00000000");
    const merchantUid = `quick_${svcKey.replace("퀵사주_", "")}_${Date.now()}`;
    IMP.request_pay(
      {
        pg: "html5_inicis",
        pay_method: "card",
        merchant_uid: merchantUid,
        name: `우연 쿠키사주 — ${svc.label}`,
        amount: svc.price,
        buyer_name: person1.name,
        buyer_email: email.trim(),
      },
      (rsp: any) => {
        if (rsp.success) {
          callAnalyze(rsp.imp_uid, rsp.merchant_uid);
        } else {
          setErr(`결제 실패: ${rsp.error_msg || "다시 시도해주세요."}`);
        }
      }
    );
  }

  const accentColor = svc?.color || "#7048a8";

  /* ── 분석/결제 로딩 ─────────────────────────────────────── */
  if (previewing || (paying && !result)) {
    const msg = previewing ? "사주를 분석하는 중입니다" : "결제 후 리포트 생성 중...";
    return (
      <>
        <Global />
        <DancheongBar />
        <Wrap>
          <div style={{ textAlign: "center", padding: "80px 16px" }}>
            <img src="/woo_y_3.png" alt="분석 중"
              style={{ width: 110, height: 110, objectFit: "contain",
                filter: "drop-shadow(0 8px 24px rgba(200,144,30,0.3))",
                animation: `floatQ 2s ease-in-out infinite` }} />
            <div style={{ fontFamily: "'Noto Serif KR', serif", fontSize: 15, fontWeight: 900, color: "#2C1810", marginBottom: 8 }}>{msg}</div>
            <div style={{ fontSize: 12, color: "#9c7b5a" }}>잠시만 기다려주세요.</div>
          </div>
        </Wrap>
      </>
    );
  }

  /* ── 미리보기 화면 ───────────────────────────────────────── */
  if (preview && !result) {
    return (
      <>
        <Global />
        <DancheongBar />
        <Wrap>
          <NavBar>
            <NavBtn onClick={() => setPreview(null)}>← 돌아가기</NavBtn>
            <PageTitle>{svc?.label} 미리보기</PageTitle>
          </NavBar>
          <div style={{ padding: "16px 16px 0" }}>
            {/* 미리보기 카드 */}
            <div style={{
              background: "#fff", borderRadius: 20,
              border: `2px solid ${accentColor}33`,
              overflow: "hidden",
              boxShadow: `0 8px 32px ${accentColor}18`,
            }}>
              <div style={{ height: 5, background: `linear-gradient(90deg, ${accentColor}, ${accentColor}88)` }} />
              <div style={{ padding: "20px 18px 0" }}>
                <div style={{
                  fontSize: "0.82rem", fontWeight: 800,
                  color: accentColor, marginBottom: 12, letterSpacing: 1,
                }}>
                  {svc?.label} — 미리보기
                </div>
                {/* 미리보기 텍스트 — 짧아서 스크롤 불필요 */}
                <div style={{
                  position: "relative",
                  paddingBottom: 0,
                }}>
                  <div style={{
                    fontSize: "0.9rem", color: "#2C1810", lineHeight: 2.1,
                    whiteSpace: "pre-line", wordBreak: "keep-all",
                    textAlign: "left",
                  }}>
                    {preview.text}
                  </div>
                  {/* 끝 부분 페이드 */}
                  <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0, height: 60,
                    background: "linear-gradient(to bottom, transparent, #fff)",
                    pointerEvents: "none",
                  }} />
                </div>
              </div>
              {/* 잠금 + 결제 */}
              <div style={{ padding: "12px 18px 20px", textAlign: "center", borderTop: `1px solid ${accentColor}18`, marginTop: 8 }}>
                <img src="/woo_y_4.png" alt="" style={{ width: 60, height: 60, objectFit: "contain", filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.15))", marginBottom: 4 }} />
                <div style={{ fontSize: "0.88rem", fontWeight: 900, color: "#2C1810", marginBottom: 4 }}>
                  전체 내용은 결제 후 이메일로 받아보세요
                </div>
                <div style={{ fontSize: "0.72rem", color: "#9c7b5a", marginBottom: 14 }}>
                  결제 즉시 {email} 로 발송
                </div>
                <button onClick={handlePay} style={{
                  width: "100%", padding: "14px",
                  background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                  color: "#fff", border: "none", borderRadius: 50,
                  fontSize: "1rem", fontWeight: 900, cursor: "pointer",
                  fontFamily: "inherit",
                  boxShadow: `0 4px 20px ${accentColor}44`,
                }}>
                  ₩{preview.price.toLocaleString()} — 전체 결과 받기
                </button>
                <div style={{ fontSize: "0.7rem", color: "#a08060", marginTop: 10 }}>
                  결제 전 <a href="/legal/refund" target="_blank" rel="noreferrer"
                    style={{ color: accentColor, textDecoration: "underline" }}>환불정책</a> 확인
                  &nbsp;·&nbsp; 열람 전 7일 이내 전액 환불
                </div>
              </div>
            </div>
          </div>
          <SiteFooter />
        </Wrap>
      </>
    );
  }

  /* ── 결과 화면 ───────────────────────────────────────────── */
  if (result) {
    return (
      <>
        <Global />
        <DancheongBar />
        <Wrap>
          <NavBar>
            <NavBtn onClick={() => navigate("/")}>← 홈</NavBtn>
            <PageTitle style={{ fontFamily: "'Noto Serif KR', serif" }}>쿠키사주</PageTitle>
          </NavBar>

          <div style={{ textAlign: "center", padding: "28px 16px 8px" }}>
            <ResultHeroImg src="/woo_y_1.png" alt="분석 완료" />
            <div style={{ fontFamily: "'Noto Serif KR', serif", fontSize: "1.4rem", fontWeight: 900, color: "#2C1810" }}>분석 완료!</div>
          </div>

          <ResultCard>
            {result.emailSent && (
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: `${accentColor}12`, border: `1.5px solid ${accentColor}35`,
                borderRadius: 14, padding: "12px 16px", marginBottom: 16, textAlign: "left",
              }}>
                <span style={{ fontSize: "1.3rem" }}>📩</span>
                <div style={{ fontSize: 13, color: "#2d1f3d", lineHeight: 1.6 }}>
                  <strong style={{ color: accentColor }}>{email}</strong>으로 결과지가 발송되었습니다.<br />
                  <span style={{ fontSize: 11, color: "#9a8aaa" }}>메일이 안 보이면 스팸 폴더를 확인하세요.</span>
                </div>
              </div>
            )}

            {result.reportHtml ? (
              <iframe
                srcDoc={result.reportHtml}
                title="쿠키사주 결과"
                style={{
                  width: "100%", border: "none", borderRadius: 16,
                  minHeight: 700, display: "block",
                  boxShadow: "0 4px 24px rgba(74,32,96,0.1)",
                  marginBottom: 16,
                }}
                onLoad={e => {
                  const iframe = e.currentTarget;
                  try {
                    const h = iframe.contentDocument?.documentElement?.scrollHeight;
                    if (h && h > 100) iframe.style.height = h + 40 + "px";
                  } catch { /* cross-origin 무시 */ }
                }}
              />
            ) : (
              <div style={{ fontSize: 15, fontWeight: 800, color: "#2d1f3d", padding: "24px 0" }}>
                분석이 완료되었습니다!<br />
                <span style={{ fontSize: 13, fontWeight: 400, color: "#7a6a8a" }}>
                  결과가 이메일로 발송되었습니다.
                </span>
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => { setResult(null); setStep(0); setSvcKey(""); setPaying(false); setErr(""); }}
                style={{
                  flex: 1, padding: "13px",
                  background: "#fff", border: "1.5px solid #d8cfe8",
                  borderRadius: 14, fontSize: 14, fontWeight: 700,
                  color: "#5a4a72", cursor: "pointer", fontFamily: "inherit",
                }}>
                다른 운세 보기
              </button>
              <button
                onClick={() => navigate("/client")}
                style={{
                  flex: 1, padding: "13px",
                  background: `linear-gradient(135deg,${accentColor},${accentColor}cc)`,
                  border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800,
                  color: "#fff", cursor: "pointer", fontFamily: "inherit",
                }}>
                상세 사주 보기
              </button>
            </div>
          </ResultCard>
          <SiteFooter />
        </Wrap>
      </>
    );
  }

  return (
    <>
      <Global />
      {/* ── 시주 추정 설문 모달 ─────────────────────────── */}
      {surveying && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
          onClick={e => { if (e.target === e.currentTarget) closeSurvey(); }}>
          <div style={{background:"#fff",borderRadius:18,width:"100%",maxWidth:520,maxHeight:"88vh",overflowY:"auto",padding:"26px 22px",boxShadow:"0 8px 40px rgba(0,0,0,0.22)"}}>
            <div style={{fontSize:"1rem",fontWeight:800,color:"#3d2b1f",marginBottom:4}}>출생시간 추정 설문</div>
            <div style={{fontSize:"0.78rem",color:"#9a7d6a",marginBottom:18}}>
              10개 문항으로 가능성 높은 시주를 추천해드립니다.{surveying==="p2"?" (상대방 기준)":""}
            </div>
            {surveyResults.length === 0 ? (
              <>
                {surveyQs.map((q, qi) => (
                  <div key={q.id} style={{marginBottom:18}}>
                    <div style={{fontSize:"0.83rem",fontWeight:700,color:"#3d2b1f",marginBottom:7}}>{qi+1}. {q.question}</div>
                    {q.choices.map((c, ci) => (
                      <label key={ci} onClick={() => setSurveyAns(a => { const n=[...a]; n[qi]=ci; return n; })}
                        style={{display:"flex",alignItems:"flex-start",gap:8,padding:"8px 12px",borderRadius:8,cursor:"pointer",fontSize:"0.8rem",marginBottom:5,
                          background:surveyAns[qi]===ci?"#FFF3E0":"#F8F4F0",
                          border:`1.5px solid ${surveyAns[qi]===ci?"#c09a6b":"#e8dfd5"}`,
                          color:surveyAns[qi]===ci?"#7a4f2d":"#5a4535",transition:"all 0.15s"}}>
                        <input type="radio" readOnly checked={surveyAns[qi]===ci} style={{marginTop:2,accentColor:"#c09a6b",flexShrink:0}} />
                        {c}
                      </label>
                    ))}
                  </div>
                ))}
                <div style={{display:"flex",gap:10,marginTop:8}}>
                  <button onClick={closeSurvey} style={{flex:1,padding:"10px",border:"1px solid #d4c0aa",borderRadius:10,background:"#f5f0ea",cursor:"pointer",fontSize:"0.82rem",color:"#7a6654"}}>취소</button>
                  <button onClick={submitSurvey} disabled={surveyAns.some(a=>a<0)}
                    style={{flex:2,padding:"10px",border:"none",borderRadius:10,background:surveyAns.some(a=>a<0)?"#d4c0aa":"#c09a6b",color:"#fff",cursor:surveyAns.some(a=>a<0)?"not-allowed":"pointer",fontSize:"0.82rem",fontWeight:700}}>
                    {surveyAns.some(a=>a<0)?`${surveyAns.filter(a=>a<0).length}개 질문 남음`:"시주 추정하기"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{fontSize:"0.83rem",fontWeight:700,color:"#3d2b1f",marginBottom:10}}>추정 결과 (가능성 높은 순)</div>
                {surveyResults.map((r) => (
                  <div key={r.rank} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 14px",borderRadius:10,marginBottom:7,
                    background:r.rank===1?"#FFF9DB":r.rank===2?"#F3F0FF":"#F0F8FF",
                    border:`1.5px solid ${r.rank===1?"#F59F00":r.rank===2?"#9775FA":"#74C0FC"}`}}>
                    <div style={{width:26,height:26,borderRadius:"50%",background:r.rank===1?"#F59F00":r.rank===2?"#9775FA":"#74C0FC",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.75rem",fontWeight:800,flexShrink:0}}>{r.rank}</div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:800,fontSize:"0.88rem",color:"#2b1d10"}}>{r.name} ({r.hanja}) — {r.time_range}</div>
                      <div style={{fontSize:"0.75rem",color:"#888",marginTop:1}}>오행: {r.ohaeng} · {r.animal}띠 · 일치도 {r.confidence}%</div>
                    </div>
                    <button onClick={() => applySurveyResult(r.birth_hour)}
                      style={{padding:"6px 12px",border:"none",borderRadius:8,background:"#c09a6b",color:"#fff",fontSize:"0.75rem",fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                      이 시간으로 설정
                    </button>
                  </div>
                ))}
                <div style={{display:"flex",gap:10,marginTop:10}}>
                  <button onClick={() => setSurveyResults([])} style={{flex:1,padding:"9px",border:"1px solid #d4c0aa",borderRadius:10,background:"#f5f0ea",cursor:"pointer",fontSize:"0.8rem",color:"#7a6654"}}>다시 풀기</button>
                  <button onClick={closeSurvey} style={{flex:1,padding:"9px",border:"1px solid #d4c0aa",borderRadius:10,background:"#f5f0ea",cursor:"pointer",fontSize:"0.8rem",color:"#7a6654"}}>닫기</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <DancheongBar />
      <Wrap>
        {/* ── STEP 0 히어로: 전체 씬 이미지 ── */}
        {step === 0 && (
          <HeroSceneWrap>
            <HeroSceneImg src="/woo_06.png" alt="쿠키사주" />
            <HeroSceneOverlay />
            <HeroTitleBlock>
              <HeroTitle>쿠키사주</HeroTitle>
              <HeroSub>궁금한 운세 하나만 딱 골라 명리학으로 풀어드립니다</HeroSub>
            </HeroTitleBlock>
          </HeroSceneWrap>
        )}

        {/* 네비게이션 */}
        <NavBar style={{ marginTop: step === 1 ? 0 : 0 }}>
          <NavBtn onClick={() => navigate("/")}>← 홈</NavBtn>
          {step === 1 && (
            <NavBtn onClick={() => { setStep(0); setErr(""); }}>↩ 서비스 선택</NavBtn>
          )}
          <PageTitle style={{ fontFamily: "'Noto Serif KR', serif" }}>쿠키사주</PageTitle>
        </NavBar>

        {/* ── STEP 1 헤더: 선택한 서비스 씬 이미지 ── */}
        {step === 1 && svc && (
          <HeroSceneWrap>
            <HeroSceneImg src={svc.img || "/woo_06.png"} alt={svc.label} />
            <HeroSceneOverlay />
            <HeroTitleBlock>
              <HeroTitle>{svc.label}</HeroTitle>
              <HeroSub>{svc.price.toLocaleString()}원 · 결제 후 이메일 즉시 발송</HeroSub>
            </HeroTitleBlock>
          </HeroSceneWrap>
        )}

        {/* ── STEP 0: 서비스 선택 ── */}
        {step === 0 && (
          <>
            <SectionLabel>궁금한 운세를 선택하세요</SectionLabel>
            <CardGrid>
              {SERVICES.map((s, idx) => (
                <SvcCard key={s.key} $color={s.color} $idx={idx} onClick={() => selectService(s.key)}>
                  <SvcCardImg $src={s.img} />
                  <SvcCardBody $color={s.color}>
                    <SvcIconImg src={s.iconImg} alt={s.label} />
                    <SvcLabel $color={s.color}>{s.label}</SvcLabel>
                    <SvcDesc $color={s.color}>{s.desc}</SvcDesc>
                    <SvcPrice $color={s.color}>{s.price.toLocaleString()}원 →</SvcPrice>
                  </SvcCardBody>
                </SvcCard>
              ))}
            </CardGrid>
            <SiteFooter />
          </>
        )}

        {/* ── STEP 1: 입력 폼 ── */}
        {step === 1 && svc && (
          <FormArea>
            {/* 선택된 서비스 배지 */}
            <ServiceBadge $color={svc.color}>
              <img src={svc.iconImg} alt={svc.label} style={{ width: 40, height: 40, objectFit: "contain", filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.18))", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 900, color: svc.color, fontSize: 14 }}>
                  쿠키사주 {svc.label} — {svc.price.toLocaleString()}원
                </div>
                {svc.ageLimit && (
                  <div style={{ fontSize: 11, color: "#e03131", fontWeight: 700, marginTop: 2 }}>
                    ※ 만 20세 이상만 이용 가능
                  </div>
                )}
              </div>
            </ServiceBadge>

            {/* 내 정보 폼 */}
            <PersonFormBlock
              title="내 정보"
              data={person1}
              onChange={setPerson1}
              accentColor={svc.color}
              email={email}
              onEmailChange={setEmail}
              onSurvey={() => openSurveyFor("p1")}
            />

            {/* 상대방 정보 (연애궁합만) */}
            {svc.needPartner && (
              <>
                <div style={{ textAlign: "center", fontSize: 12, color: "#9a8aaa", margin: "4px 0 12px", fontWeight: 600 }}>
                  ↕ 상대방 정보도 입력해주세요
                </div>
                <PersonFormBlock
                  title="상대방 정보"
                  data={person2}
                  onChange={setPerson2}
                  accentColor={svc.color}
                  onSurvey={() => openSurveyFor("p2")}
                />
              </>
            )}

            {/* 이용약관 동의 */}
            <Card style={{ marginTop: 0 }}>
              <ConsentBox>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                />
                <span>
                  <a href="/legal/terms" target="_blank" rel="noreferrer" style={{ color: "#9c7b5a" }}>이용약관</a>
                  {" "}및{" "}
                  <a href="/legal/privacy" target="_blank" rel="noreferrer" style={{ color: "#9c7b5a" }}>개인정보 처리방침</a>
                  에 동의하며, 개인정보(이름, 생년월일)를 사주 분석 목적으로 수집·이용하는 것에 동의합니다. (필수)
                </span>
              </ConsentBox>
            </Card>

            {err && <ErrMsg>{err}</ErrMsg>}

            <SubmitBtn $color={svc.color} onClick={handlePreview} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <img src="/woo_y_1.png" alt="" style={{ width: 28, height: 28, objectFit: "contain", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }} />
              {svc.label} 미리보기
            </SubmitBtn>
            <div style={{ fontSize: 11, color: "#9a8aaa", textAlign: "center", marginTop: 8 }}>
              미리보기 후 결제 · 결제 즉시 이메일로 결과 발송
            </div>
            <div style={{ fontSize: 11, color: "#a08060", textAlign: "center", marginTop: 6 }}>
              결제 전 <a href="/legal/refund" target="_blank" rel="noreferrer" style={{ color: "#8a6a9a", textDecoration: "underline" }}>환불정책</a>을 확인해주세요.
              리포트 열람 전 7일 이내 전액 환불 가능합니다.
            </div>
          </FormArea>
        )}
      </Wrap>
    </>
  );
}
